// Everyday Services (code name `commerce`): airtime and data bundles for yourself
// or someone else, paid from a mobile-money number you confirm. The provider
// collects and delivers; KaziPeople never holds the money and the employer never
// sees a personal purchase. Company purchases are a separate, permission-gated
// context (commerce:company:purchase / :approve).
//
// Routes (TenantLayout): `everyday-services` (checkout + My purchases) and
// `everyday-services/orders/:id` (step 4 — the order's status; deep link target
// for notifications, and the safe place to land after a refresh).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, User } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import { useHasPermission } from '@/rbac/useRbac';
import { useAppSelector } from '@/app/hooks';
import { getApiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import { commerceApi } from '../api/commerceApi';
import { PersonalCheckout, StepTabs } from '../components/checkout/PersonalCheckout';
import { CompanyPurchasesPanel } from '../components/company/CompanyPurchasesPanel';
import { OrderStatusView } from '../components/OrderStatusView';
import { PurchasesList } from '../components/PurchasesList';
import type { Availability, Order } from '../types';

const COUNTRY = 'TZ';

export const EverydayServicesPage: React.FC = () => {
  const { t } = useTranslation('everydayServices');
  const { id: orderId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const canCompanyBuy = useHasPermission('commerce:company:purchase');
  const canCompanyApprove = useHasPermission('commerce:company:approve');
  const hasCompany = canCompanyBuy || canCompanyApprove;
  const myUserId = useAppSelector((s) => s.auth?.user?.id) as string | undefined;
  const [context, setContext] = useState<'personal' | 'company'>('personal');

  const [availability, setAvailability] = useState<Availability | null>(null);
  const [availError, setAvailError] = useState<unknown>(null);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [ordersError, setOrdersError] = useState<unknown>(null);

  const loadAvailability = useCallback(async () => {
    setAvailError(null);
    setAvailability(null);
    try {
      setAvailability(await commerceApi.availability(COUNTRY));
    } catch (e) {
      setAvailError(e);
    }
  }, []);

  const loadOrders = useCallback(async () => {
    setOrdersError(null);
    setOrders(null);
    try {
      setOrders(await commerceApi.listOrders(50));
    } catch (e) {
      setOrdersError(e);
    }
  }, []);

  useEffect(() => { void loadAvailability(); }, [loadAvailability]);
  useEffect(() => {
    if (!orderId) void loadOrders();
  }, [orderId, loadOrders]);

  const openOrder = (id: string) =>
    navigate(orderId ? `../${encodeURIComponent(id)}` : `orders/${encodeURIComponent(id)}`, { relative: 'path' });
  const backToMain = () => navigate('../..', { relative: 'path' });
  const scrollToPurchases = () => document.getElementById('es-purchases')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const header = (
    <div className="space-y-4 border-b border-border pb-6">
      <div>
        <h1 className="text-h1 text-foreground">{t('title')}</h1>
        <p className="mt-1 text-muted-foreground">{t('subtitle')}</p>
      </div>
      {hasCompany && !orderId && (
        <div role="radiogroup" aria-label={t('context.label')} className="flex flex-wrap gap-2">
          {(['personal', 'company'] as const).map((c) => (
            <button key={c} type="button" role="radio" aria-checked={context === c} onClick={() => setContext(c)}
              className={cn('flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                context === c ? 'border-primary-500 bg-accent text-accent-foreground' : 'border-border bg-card text-foreground hover:bg-muted')}>
              {c === 'personal' ? <User className="h-4 w-4" aria-hidden="true" /> : <Building2 className="h-4 w-4" aria-hidden="true" />}
              {t(`context.${c}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // Step 4 — a deep link to an order works even if availability changed since:
  // an employee must always be able to see the outcome of something they paid for.
  if (orderId) {
    return (
      <div className="space-y-6">
        {header}
        <StepTabs current="status" />
        <div>
          <OrderStatusView orderId={orderId} operators={availability?.operators ?? []} onBack={backToMain} />
        </div>
      </div>
    );
  }

  if (context === 'company' && hasCompany) {
    return (
      <div className="space-y-6">
        {header}
        <CompanyPurchasesPanel operators={availability?.operators ?? []} canPurchase={canCompanyBuy}
          canApprove={canCompanyApprove} currentUserId={myUserId} />
      </div>
    );
  }

  let buy: React.ReactNode;
  const checkoutProducts = availability?.products.filter((p) => p.productType === 'AIRTIME' || p.productType === 'DATA_BUNDLE') ?? [];
  if (availError) {
    buy = (
      <Card><CardContent className="p-0">
        <DataState
          variant={errorVariant(availError)}
          title={t('errors.availability')}
          message={getApiErrorMessage(availError, t('errors.availability'))}
          onRetry={loadAvailability}
        />
      </CardContent></Card>
    );
  } else if (!availability) {
    buy = <Card><CardContent className="p-0"><DataState variant="loading" title={t('loadingCatalogue')} /></CardContent></Card>;
  } else if (!availability.enabled || checkoutProducts.length === 0) {
    // Never says why (employer switch vs platform switch vs provider) — by design.
    buy = (
      <Card><CardContent className="p-0">
        <DataState variant="unavailable" title={t('unavailable.title')} message={t('unavailable.message')} />
      </CardContent></Card>
    );
  } else {
    buy = <PersonalCheckout availability={availability} onPlaced={(o) => openOrder(o.id)} onOpenPurchases={scrollToPurchases} />;
  }

  // Past purchases stay visible even when buying is switched off: they are the
  // employee's own records (receipts, tokens, refunds in flight).
  const showList = !!availability?.enabled || (orders?.length ?? 0) > 0 || !!ordersError;

  return (
    <div className="space-y-6">
      {header}
      {buy}
      {showList && (
        <div id="es-purchases" className="scroll-mt-4">
          <PurchasesList
            orders={orders}
            error={ordersError}
            operators={availability?.operators ?? []}
            onRetry={loadOrders}
            onOpen={openOrder}
          />
        </div>
      )}
    </div>
  );
};

export default EverydayServicesPage;
